import { Link, useParams } from "react-router-dom";
import { CheckCircle2, Lock, PlayCircle, ArrowLeft, ArrowRight, Clock, Layers, FileText, Image as ImageIcon, Video } from "lucide-react";
import { useApi } from "../../lib/api";
import { Stagger, Item, AnimatedProgress, Reveal } from "../../components/motion";
import { ErrorState, Badge } from "../../components/common";

export const TYPE_ICON = { video: Video, text: FileText, pdf: FileText, image: ImageIcon };

export function LessonRow({ l, cid, current }) {
  const Icon = l.status === "completed" ? CheckCircle2 : l.status === "locked" ? Lock : PlayCircle;
  const TIcon = TYPE_ICON[l.type] || FileText;
  const tone = l.status === "completed" ? "text-emerald-500" : l.status === "locked" ? "text-slate-300" : "text-[var(--brand)]";
  const inner = (
    <div className={`group flex items-center gap-3 rounded-xl px-3.5 py-3 transition-colors ${current ? "bg-[var(--brand-50)] ring-1 ring-[rgba(227,10,23,0.15)]" : l.status === "locked" ? "opacity-70" : "hover:bg-slate-50"}`}>
      <Icon className={`h-5 w-5 shrink-0 ${tone}`} strokeWidth={1.8} />
      <span className={`flex-1 text-sm ${l.status === "locked" ? "text-slate-400" : "font-medium text-[var(--ink)]"}`}>{l.title}</span>
      <TIcon className="h-4 w-4 text-slate-300" />
      {l.status !== "locked" && <ArrowRight className="h-4 w-4 text-slate-300 transition-transform group-hover:translate-x-0.5 group-hover:text-[var(--brand)]" />}
    </div>
  );
  if (l.status === "locked") return <div data-testid={`lesson-row-${l.id}`} data-status="locked" title="Önceki dersleri tamamlayın">{inner}</div>;
  return <Link to={`/panel/egitimlerim/${cid}/ders/${l.id}`} data-testid={`lesson-row-${l.id}`} data-status={l.status}>{inner}</Link>;
}

export default function CourseDetail() {
  const { courseId } = useParams();
  const { data: c, loading, error, reload } = useApi(`/me/courses/${courseId}`);
  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (loading) return <div className="space-y-5"><div className="skeleton h-64" /><div className="skeleton h-96" /></div>;
  const next = c.modules.flatMap((m) => m.lessons).find((l) => l.status === "active");
  return (
    <div>
      <Link to="/panel/egitimlerim" className="group mb-5 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-[var(--ink)]" data-testid="back-to-courses-link">
        <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" /> Eğitimlerim
      </Link>
      <Reveal className="surface overflow-hidden">
        <div className="grid lg:grid-cols-[1fr_380px]">
          <div className="p-6 sm:p-8">
            <div className="flex flex-wrap gap-1.5"><Badge status={c.state} />{c.is_demo && <Badge status="demo" />}</div>
            <h1 className="mt-3 text-2xl sm:text-3xl font-extrabold tracking-tight text-[var(--ink)]" data-testid="course-detail-title">{c.title}</h1>
            <p className="mt-3 max-w-2xl text-sm sm:text-base leading-relaxed text-slate-500">{c.description}</p>
            <div className="mt-5 flex flex-wrap gap-4 text-sm text-slate-500">
              <span className="inline-flex items-center gap-1.5"><Layers className="h-4 w-4" /> {c.modules.length} modül · {c.lesson_count} ders</span>
              {c.duration && <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4" /> {c.duration}</span>}
            </div>
            <div className="mt-7 max-w-md">
              <div className="mb-2 flex justify-between text-sm font-semibold"><span className="text-slate-500">İlerleme</span><span data-testid="course-detail-progress">%{c.progress}</span></div>
              <AnimatedProgress value={c.progress} height="h-2.5" />
            </div>
            {next && (
              <Link to={`/panel/egitimlerim/${c.id}/ders/${next.id}`} className="btn-primary group/cta mt-7 inline-flex h-12 w-full sm:w-auto items-center justify-center gap-2 rounded-xl px-6 text-sm font-semibold" data-testid="course-detail-continue-btn">
                {c.progress === 0 ? "Eğitime Başla" : "Kaldığın Yerden Devam Et"} <ArrowRight className="h-4 w-4 transition-transform group-hover/cta:translate-x-1" />
              </Link>
            )}
          </div>
          {c.cover_url && <img src={c.cover_url} alt={c.title} className="h-56 w-full object-cover lg:h-full" />}
        </div>
      </Reveal>
      <Stagger className="mt-8 space-y-5" gap={0.08} delay={0.15}>
        {c.modules.map((m, i) => (
          <Item key={m.id} className="surface p-3 sm:p-4" data-testid={`module-${m.id}`}>
            <div className="flex items-center justify-between px-3.5 pb-2 pt-1">
              <h3 className="text-sm font-bold text-[var(--ink)]"><span className="mr-2 font-mono text-xs text-slate-400">{String(i + 1).padStart(2, "0")}</span>{m.title}</h3>
              <span className="text-xs font-semibold text-slate-400">{m.lessons.filter((l) => l.status === "completed").length}/{m.lessons.length}</span>
            </div>
            {m.lessons.map((l) => <LessonRow key={l.id} l={l} cid={c.id} />)}
          </Item>
        ))}
      </Stagger>
    </div>
  );
}
